import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from '../config/api';
import { useAuth } from '../context/AuthContext';

export default function DeleteAccountPanel() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [confirm, setConfirm] = useState(false);
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  if (user?.isGuest) {
    return (
      <p className="text-sm text-gray-600">
        Guest visits are not saved as accounts. Sign out to leave this visit.
      </p>
    );
  }

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    setError('');
    if (!confirm) {
      setError('Tick the box to confirm you want to delete your account.');
      return;
    }
    if (!password) {
      setError('Re-enter your password.');
      return;
    }
    setBusy(true);
    try {
      await axios.post('/api/auth/delete-account', { confirm: true, password });
      logout();
      navigate('/delete-account', { replace: true, state: { deleted: true } });
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Could not delete the account. Try again.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} className="space-y-3">
      <p className="text-sm text-gray-700">
        This removes your name, email, phone, addresses, photos, push notifications, and wallet or
        points. You will not be able to sign in. Past orders stay on record without those personal
        details so cafés can still be paid correctly.
      </p>
      {error && <p className="text-sm text-red-700">{error}</p>}
      <label className="flex items-start gap-2 text-sm text-gray-800">
        <input
          type="checkbox"
          checked={confirm}
          onChange={(event) => setConfirm(event.target.checked)}
          className="mt-1"
        />
        <span>I understand this cannot be undone.</span>
      </label>
      <div>
        <label className="block text-sm text-gray-700 mb-1" htmlFor="delete-account-password">
          Re-enter your password
        </label>
        <input
          id="delete-account-password"
          type="password"
          value={password}
          onChange={(event) => setPassword(event.target.value)}
          autoComplete="current-password"
          className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500"
        />
      </div>
      <button
        type="submit"
        disabled={busy}
        className="bg-red-700 text-white px-4 py-2 rounded-lg font-semibold disabled:opacity-50"
      >
        {busy ? 'Deleting…' : 'Delete my account'}
      </button>
    </form>
  );
}
